import CloseIcon from "@assets/svg/CloseIcon";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
} from "@components/ui/dialog";
import { Dispatch, SetStateAction } from "react";
import {
  Briefcase,
  CheckCircle2,
  Clock,
  Gauge,
  InfoIcon,
  Target,
} from "lucide-react";
import { CircularMatchRating } from "@pages/candidate/AppliedJobDetailsNew/components/CircularMatchRating";

interface SkillDetailsModalProps {
  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
}

const skillAspects = [
  {
    label: "Proficiency Level",
    icon: Gauge,
    jobRequirement: {
      details: "Advanced",
      level: "Advanced",
    },
    yourExperience: {
      details: "Advanced",
      level: "Advanced",
      matchPercentage: 100,
    },
  },
  {
    label: "Experience",
    icon: Briefcase,
    jobRequirement: {
      details: "3+ years",
      level: null,
    },
    yourExperience: {
      details: "4 years 2 months",
      level: null,
      matchPercentage: 100,
    },
  },
  {
    label: "Recency",
    icon: Clock,
    jobRequirement: {
      details: "Within last 6 months",
      level: null,
    },
    yourExperience: {
      details: "2 weeks ago",
      level: null,
      matchPercentage: 100,
    },
  },
  {
    label: "Relevance",
    icon: Target,
    jobRequirement: {
      details: "Highly relevant",
      level: null,
    },
    yourExperience: {
      details: "Highly relevant",
      level: null,
      matchPercentage: 80,
    },
  },
];

const SkillDetailsModal = ({
  openModal,
  setOpenModal,
}: SkillDetailsModalProps) => {
  return (
    <Dialog open={openModal} onOpenChange={setOpenModal}>
      <DialogContent
        showCloseButton={false}
        className="!flex flex-col bg-white !max-w-5xl max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex justify-between">
          <div className="flex flex-col gap-1">
            <span className="font-bold text-sm">Skill Match Details</span>
            <span className="font-semibold text-xs text-primary">
              See how your skills and experience match this job requirement.
            </span>
          </div>
          <DialogClose asChild>
            <button
              onClick={() => {
                setOpenModal(false);
              }}
              className="h-max p-2 rounded-md hover:bg-gray-50"
            >
              <CloseIcon className={"w-5 h-5"} />
            </button>
          </DialogClose>
        </div>
        <div className="rounded-md flex flex-col gap-3 min-h-0 flex-1 overflow-y-scroll no-scrollbar">
          {/* Skill match description */}
          <div className="border rounded-md grid grid-cols-[1.15fr_0.6fr_1.15fr]">
            {/* Col 1 */}
            <div className="flex flex-col gap-3 py-3 px-4">
              <div className="uppercase text-xss font-bold px-3 py-0.5 bg-blue-50 text-blue-700 h-max w-max rounded-md">
                job requirement
              </div>
              <div className="flex flex-col gap-5">
                <span className="font-bold text-charcoal">React</span>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-bold text-charcoal">
                    Description
                  </span>
                  <span className="text-xss font-semibold text-primary">
                    Hands-on experience with React, hooks, context API and
                    component lifecycle.
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-bold text-charcoal">
                    Proficiency Level
                  </span>
                  <div className="flex items-center gap-1.5">
                    <div className="h-2 w-2 rounded-full bg-red-500" />
                    <span className="text-xss font-semibold text-primary">
                      Advanced
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-bold text-charcoal">
                    Experience Required
                  </span>
                  <span className="text-xs font-bold text-charcoal">
                    3+ years
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-bold text-charcoal">
                    Last Used (Expectation)
                  </span>
                  <span className="text-xss font-bold text-primary">
                    Within last 6 months
                  </span>
                </div>
              </div>
            </div>
            {/* Col 2 */}
            <div className="flex flex-col items-center border-x p-8 gap-6">
              <div className="flex flex-col items-center gap-3">
                <span className="font-bold text-xsss">Your Match</span>
                <CircularMatchRating
                  percentage={78}
                  showLabel={false}
                  size={70}
                />
                <span className="font-bold text-xss">Excellent</span>
              </div>

              <div className="flex gap-2">
                <CheckCircle2 size={11} className="shrink-0 text-red-700" />
                <span className="font-semibold text-[9px]">
                  Strong match across all key areas
                </span>
              </div>

              <div className="flex gap-2">
                <InfoIcon size={11} className="shrink-0" />
                <span className="font-semibold text-[9px]">
                  A higher match means a closer alignment with the requirement.
                </span>
              </div>
            </div>
            {/* Col 3 */}
            <div className="flex flex-col gap-3 py-3 px-4">
              <div className="uppercase text-xss font-bold px-3 py-0.5 bg-red-50 text-red-700 w-max h-max rounded-md">
                your experience
              </div>
              <div className="flex flex-col gap-5">
                <span className="font-bold text-charcoal">React</span>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-bold text-charcoal">
                    Description
                  </span>
                  <span className="text-xss font-semibold text-primary">
                    Developed multiple React applications using hooks, Context
                    API, Redux and functional components. Buitt reusable
                    component libraries and optimized performance.
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-bold text-charcoal">
                    Proficiency Level
                  </span>
                  <div className="flex items-center gap-1.5">
                    <div className="h-2 w-2 rounded-full bg-red-500" />
                    <span className="text-xss font-semibold text-primary">
                      Advanced
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-bold text-charcoal">
                    Total Experience
                  </span>
                  <span className="text-xs font-bold text-charcoal">
                    4 years 2 months
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-bold text-charcoal">
                    Last Used
                  </span>
                  <span className="text-xss font-bold text-primary">
                    2 weeks ago
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* Skill match breakdown */}
          <div className="flex flex-col gap-2 mt-4">
            <span className="font-bold text-xss">Skills Match Breakdown </span>
            {/* Table */}
            <div className="border rounded-md">
              {/* Table header */}
              <div className="grid grid-cols-[0.6fr_1fr_1.4fr]">
                <div className="px-4 flex flex-col justify-end py-3">
                  <span className="font-bold text-xss text-primary">
                    Skill Aspect
                  </span>
                </div>
                <div className="border-x px-16 flex flex-col items-center py-3 gap-3">
                  <div className="uppercase text-xsss font-bold bg-blue-50 text-blue-700 rounded-md py-1 px-2 w-max h-max">
                    job requirement
                  </div>
                  <div className="flex items-center w-full justify-between">
                    <span className="font-bold text-xss text-primary">
                      Details
                    </span>
                    <span className="font-bold text-xss text-primary">
                      Level
                    </span>
                  </div>
                </div>
                <div className="px-12 flex flex-col items-center py-3 gap-3">
                  <div className="uppercase text-xsss font-bold bg-red-50 text-red-700 rounded-md py-1 px-2 w-max h-max">
                    your experience
                  </div>
                  <div className="flex items-center justify-between w-full">
                    <span className="font-bold text-xss text-primary">
                      Details
                    </span>
                    <span className="font-bold text-xss text-primary">
                      Level
                    </span>
                    <span className="font-bold text-xss text-primary">
                      MATCH(%)
                    </span>
                  </div>
                </div>
              </div>
              {/* Table Rows */}
              {skillAspects?.map((skillAspect) => {
                return (
                  <div className="grid grid-cols-[0.6fr_1fr_1.4fr] border-t">
                    <div className="px-4 flex items-center gap-2">
                      <skillAspect.icon className="h-3.5 w-3.5 shrink-0 text-primary" />
                      <span className="font-semibold text-xss text-primary">
                        {skillAspect?.label}
                      </span>
                    </div>

                    <div className="flex border-x items-center w-full justify-between">
                      <span className="font-semibold flex-1 text-xss text-primary text-center">
                        {skillAspect?.jobRequirement?.details}
                      </span>
                      <span className="font-semibold flex-1 text-xss text-primary text-center">
                        {skillAspect?.jobRequirement?.level || "-"}
                      </span>
                    </div>

                    <div className="flex flex-col items-center gap-3 py-1">
                      <div className="flex items-center justify-between w-full">
                        <span className="font-semibold flex-1 text-center text-xss text-primary">
                          {skillAspect?.yourExperience?.details}
                        </span>
                        <span className="font-semibold flex-1 text-center text-xss text-primary">
                          {skillAspect?.yourExperience?.level || "-"}
                        </span>
                        <div className="flex-1 flex items-center justify-center">
                          <CircularMatchRating
                            showLabel={false}
                            size={35}
                            percentage={
                              skillAspect?.yourExperience?.matchPercentage
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={() => setOpenModal(false)}
            className="font-bold text-sm border-primary border-2 text-primary py-1.5 px-5 rounded-md flex items-center justify-self-end"
          >
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SkillDetailsModal;
